import React, { useEffect, useState } from 'react';
import { useTransactionsCollection } from '../../../hooks/useFirestore';
import { formatCurrency, formatDate } from '../../../lib/utils/helpers';

interface TransactionListProps {
  institutionId?: string;
}

const TransactionList: React.FC<TransactionListProps> = ({ institutionId }) => {
  const { 
    data: transactionsData, 
    loading, 
    getDocuments 
  } = useTransactionsCollection();

  const [institutionTransactions, setInstitutionTransactions] = useState<any[]>([]);

  useEffect(() => {
    if (institutionId) {
      loadTransactions();
    }
  }, [institutionId]);

  const loadTransactions = async () => {
    if (!institutionId) return;
    
    try {
      await getDocuments({
        field: 'institutionId',
        operator: '==',
        value: institutionId,
        orderByField: 'createdAt',
        orderDir: 'desc',
        limitCount: 10 // Limiter aux 10 dernières transactions
      });
    } catch (error) {
      console.error('Error loading transactions:', error);
    }
  };

  useEffect(() => {
    if (transactionsData) {
      setInstitutionTransactions(transactionsData);
    }
  }, [transactionsData]);

  if (loading && institutionTransactions.length === 0) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (institutionTransactions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Aucune transaction trouvée
      </div>
    );
  }

  return (
    <div className="overflow-x-auto bg-white rounded-lg shadow">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Date
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Description
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Catégorie
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Montant
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Statut
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {institutionTransactions.map((transaction: any) => (
            <tr key={transaction.id}>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatDate(transaction.createdAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {transaction.description}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  transaction.type === 'income' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {transaction.type === 'income' ? 'Entrée' : 'Sortie'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {transaction.category || 'Non spécifiée'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <span className={transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}>
                  {transaction.type === 'income' ? '+' : '-'}
                  {formatCurrency(transaction.amount, transaction.currency?.code || 'XOF')}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  transaction.status === 'approved' 
                    ? 'bg-green-100 text-green-800' 
                    : transaction.status === 'pending'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {transaction.status === 'approved' ? 'Approuvé' : 
                   transaction.status === 'pending' ? 'En attente' : 'Rejeté'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TransactionList;